import { BreadcrumbObjectType } from './prisma-duplicate-types.js'

export enum TableSettingsTabType {
  CONFIGURATION = 'configuration',
  FIELDS = 'fields',
  ACTIONS = 'actions',
  EXPORTS = 'exports',
  DEPENDENCIES = 'dependencies',
}

export enum TableDetailsTabType {
  TABLE_TABS = 'tableTabs',
  PIVOT_TABLES = 'pivotTables',
}

export enum TableSettingsFieldsTabType {
  ALL_FIELDS = 'all',
  GROUPS = 'groups',
  CHILD_TABLES = 'childTables',
  COMPOSITE_KEYS = 'compositeKeys',
}

export enum FormEditTabType {
  FORM_CONTENT = 'formContent',
  SETTINGS = 'settings',
}

export enum FormSettingsTabType {
  DETAILS = 'details',
  WORKFLOWS = 'workflows',
  REDIRECT = 'redirect',
  SHARING = 'sharing',
}

/**
 * Gets navigation path for admin URLs
 *
 * If an organization ID is provided, the path will be prefixed with `/organization/:organizationId`
 * For all other types of objects, the path will append the remaining part of the path
 */
export function getAdminPath(
  type: BreadcrumbObjectType | 'GeneratedFile',
  objectId: string,
  organizationId?: string,
): string {
  let path = ''
  if (organizationId) {
    path = `/organization/${organizationId}`
  }
  switch (type) {
    case BreadcrumbObjectType.CHART:
      path += `/chart/${objectId}`
      break
    case BreadcrumbObjectType.ORGANIZATION:
      path = `/organization/${objectId}`
      break
    case BreadcrumbObjectType.PROJECT:
      path += `/project/${objectId}`
      break
    case BreadcrumbObjectType.TABLE:
      path += `/table/${objectId}`
      break
    case BreadcrumbObjectType.TABLE_ROW:
      path += `/table-row/${objectId}`
      break
    case BreadcrumbObjectType.DOCUMENT:
      path += `/document/${objectId}`
      break
    case BreadcrumbObjectType.ACTION:
      path += `/action/${objectId}`
      break
    case BreadcrumbObjectType.ACTION_STEP:
      path += `/action-step/${objectId}`
      break
    case BreadcrumbObjectType.USER:
      path += `/user/${objectId}`
      break
    case BreadcrumbObjectType.FORM:
      path += `/form/${objectId}`
      break
    case BreadcrumbObjectType.FORM_V2:
      path += `/formv2/${objectId}`
      break
    case BreadcrumbObjectType.WORKFLOW:
      path += `/workflow/${objectId}`
      break
    case BreadcrumbObjectType.WORKFLOW_V2:
      path += `/configbase/workflow/${objectId}`
      break
    case BreadcrumbObjectType.WORKFLOW_V2_TRIGGER:
      path += `/configbase/trigger/${objectId}`
      break
    case BreadcrumbObjectType.WORKSPACE:
      path += `/workspace/${objectId}`
      break
    case 'GeneratedFile':
      path += `/generated-file/${objectId}`
      break
    case BreadcrumbObjectType.EMBED:
    case BreadcrumbObjectType.TABLE_COLUMN:
    case BreadcrumbObjectType.TABLE_CELL:
    case BreadcrumbObjectType.FORM_PAGE:
    case BreadcrumbObjectType.FORM_CATEGORY:
    case BreadcrumbObjectType.FORM_FIELD:
    case BreadcrumbObjectType.FORM_V2_INPUT:
    case BreadcrumbObjectType.PIVOT_TABLE:
      break
  }
  return path
}

// Generic input for getting a web path
export interface UnifiedWebPathInput {
  objectType: BreadcrumbObjectType | 'SetPassword' | 'ResetPassword'
  objectId?: string
  commentId?: string
  workspaceId?: string
  workflow?: {
    id?: string
    actionId?: string
  }
  tableParams?: {
    tab: TableDetailsTabType
    id?: string
  }
  settingsParams?: {
    tab?: TableSettingsTabType | FormEditTabType
    settingsTab?: FormSettingsTabType
    fieldsTab?: TableSettingsFieldsTabType
    groupId?: string
  }
  token?: string
  oobCode?: string
  settings?: boolean
  home?: boolean
  creating?: boolean
  createChildTable?: boolean
  readOnly?: boolean
  isUnauth?: boolean
  variantId?: string
}

/**
 * Defines the types of paths that can be navigated to on Web
 */
export type WebPathInput =
  | { objectType: BreadcrumbObjectType.ORGANIZATION }
  | {
      objectType: BreadcrumbObjectType.FORM
      objectId: string
      home?: boolean
      settings?: boolean
      settingsParams?: {
        tab: FormEditTabType
        settingsTab: FormSettingsTabType
      }
      variantId?: string
    }
  | {
      objectType: BreadcrumbObjectType.TABLE
      objectId: string
      workflow?: {
        id?: string
      }
      settings?: boolean
      createChildTable?: boolean
      tableParams?: {
        tab?: TableDetailsTabType
        id?: string
      }
      settingsParams?: {
        tab: TableSettingsTabType
        fieldsTab: TableSettingsFieldsTabType
        groupId?: string
      }
    }
  // For route the table create page `/table/new?workspaceId=${workspaceId}`
  | {
      objectType: BreadcrumbObjectType.TABLE
      workspaceId: string
    }
  | {
      objectType: BreadcrumbObjectType.PROJECT | BreadcrumbObjectType.TABLE_ROW
      objectId: string
      commentId?: string
      workflow?: {
        id?: string
      }
    }
  | {
      objectType: BreadcrumbObjectType.DOCUMENT
      objectId: string
      readOnly?: boolean
    }
  | {
      objectType: BreadcrumbObjectType.EMBED
      objectId: string
    }
  | {
      objectType: Exclude<
        BreadcrumbObjectType,
        | BreadcrumbObjectType.FORM
        | BreadcrumbObjectType.ORGANIZATION
        | BreadcrumbObjectType.PROJECT
        | BreadcrumbObjectType.TABLE
        | BreadcrumbObjectType.TABLE_ROW
        | BreadcrumbObjectType.DOCUMENT
        | BreadcrumbObjectType.EMBED
      >
    }

/**
 * Gets navigation path for web endpoints
 */
export function getWebPath(input?: UnifiedWebPathInput): string {
  if (!input) {
    return '/'
  }

  if (input.workflow) {
    let path = `/workflows`
    if (input.workflow.id) {
      path += `/${input.workflow.id}`
      if (input.workflow.actionId) {
        const queryParams = new URLSearchParams()
        queryParams.set('action', input.workflow.actionId)
        path += `?${queryParams.toString()}`
      }
    }
    return path
  }

  if (input.objectType === 'SetPassword') {
    return '/auth/set-password' + (input.token ? `?token=${input.token}` : '')
  }

  if (input.objectType === 'ResetPassword') {
    return '/auth/reset-password' + (input.oobCode ? `?oobCode=${input.oobCode}` : '')
  }

  if (isDashboardWebPathInput(input)) {
    if (input.creating) {
      return `/workspace/new?workspaceId=${input.objectId}`
    }
    return `/workspace/${input.objectId}`
  }
  if (isDocumentWebPathInput(input)) {
    if (input.isUnauth) {
      return `/shared/document/${input.objectId}`
    }
    const path = `/document/${input.objectId}`
    return path
  }
  if (isProjectWebPathInput(input)) {
    return `/project/${input.objectId}`
  }
  if (isEmbedWebPathInput(input)) {
    return `/embed/${input.objectId}`
  }
  if (isTableRowWebPathInput(input)) {
    // Path to record
    const basePath = `/record/${input.objectId}`

    // Build query parameters
    const query = new URLSearchParams()
    if (input.commentId) {
      query.set('commentId', input.commentId)
    }
    if (input.workspaceId) {
      query.set('directory', input.workspaceId)
    }

    // Only append if there's anything to add
    const queryString = query.toString()
    return queryString ? `${basePath}?${queryString}` : basePath
  }
  if (isFormsHomeWebPathInput(input)) {
    return '/forms'
  }
  if (isNewChartWebPathInput(input)) {
    return `/chart/new?workspaceId=${input.workspaceId}`
  }
  if (isChartSettingsWebPathInput(input)) {
    return `/chart/${input.objectId}/settings`
  }
  if (isChartPageWebPathInput(input)) {
    return `/chart/${input.objectId}`
  }

  if (isPivotTableSettingsWebPathInput(input)) {
    return `/pivot/${input.objectId}/settings`
  }
  if (isPivotTablePageWebPathInput(input)) {
    return `/pivot/${input.objectId}`
  }

  if (isFormWebPathInput(input)) {
    if ('readOnly' in input) {
      return `/shared/form/${input.objectId}`
    }
    let path = '/form'
    if ('creating' in input) {
      // If input has creating, it means we're dealing with the new form creation case without
      // a defined workspace
      path += '/new'
    } else if ('workspaceId' in input) {
      path += `/new?workspaceId=${input.workspaceId}`
    } else if ('objectId' in input) {
      path += `/${input.objectId}`

      const queryParams = new URLSearchParams()

      if ('settings' in input && input.settings) {
        path += `/edit`

        if (input.settingsParams?.tab) {
          queryParams.set('tab', input.settingsParams.tab)
        }
        if (input.settingsParams?.settingsTab) {
          queryParams.set('settingsTab', input.settingsParams.settingsTab)
        }
      }

      if (input.variantId) {
        queryParams.set('variant', input.variantId)
      }

      const queryString = queryParams.toString()
      if (queryString) {
        path += `?${queryString}`
      }
    }
    return path
  }

  if (isTableWebPathInput(input)) {
    let path = '/table'

    // Use a type assertion to tell TypeScript the specific shape you're working with
    if ('workspaceId' in input) {
      // If input has workspaceId, it means we're dealing with the new table creation case
      path += `/new?workspaceId=${input.workspaceId}`
    } else if ('objectId' in input) {
      // Otherwise, if objectId exists, proceed with the standard table path logic
      path += `/${input.objectId}`
      if (input.settings) {
        path += `/settings`

        const queryParams = new URLSearchParams()
        // Ensure tab is set to fields if fieldsTab exists
        if (input.settingsParams?.fieldsTab) {
          queryParams.set('tab', TableSettingsTabType.FIELDS)
          queryParams.set('fields', input.settingsParams.fieldsTab)
          if (input.settingsParams.groupId) {
            queryParams.set('groupId', input.settingsParams.groupId)
          }
        } else if (input.settingsParams?.tab) {
          queryParams.set('tab', input.settingsParams.tab)
        }

        path += `?${queryParams.toString()}`
      } else if (input.createChildTable) {
        path += '/new'
      } else if (input.tableParams) {
        // This is the section handling tableParams for different tab types
        const queryParams = new URLSearchParams()

        // First set the tab type
        if (input.tableParams.tab) {
          queryParams.set('tab', input.tableParams.tab)
        }

        // Then set the appropriate ID parameter based on tab type
        if (input.tableParams.tab === TableDetailsTabType.TABLE_TABS) {
          if (input.tableParams.id) {
            queryParams.set('view', input.tableParams.id)
          }
        } else if (input.tableParams.tab === TableDetailsTabType.PIVOT_TABLES) {
          if (input.tableParams.id) {
            queryParams.set('pivot', input.tableParams.id)
          }
        }

        // Only append the query string if we have parameters
        const queryString = queryParams.toString()
        if (queryString) {
          path += `?${queryString}`
        }
      }
    }
    return path
  }

  // Default path is '/' for all other breadcrumb object types since their input does not include
  // an object id or workspace id or workflow
  return '/'
}

export function isChartPageWebPathInput(input: UnifiedWebPathInput): input is {
  objectType: BreadcrumbObjectType.CHART
  objectId: string
} {
  return (
    input.objectType === BreadcrumbObjectType.CHART &&
    typeof input.objectId === 'string' &&
    !input.settings
  )
}

export function isEmbedWebPathInput(input: UnifiedWebPathInput): input is {
  objectType: BreadcrumbObjectType.EMBED
  objectId: string
} {
  return input.objectType === BreadcrumbObjectType.EMBED && typeof input.objectId === 'string'
}

export function isChartSettingsWebPathInput(input: UnifiedWebPathInput): input is {
  objectType: BreadcrumbObjectType.CHART
  objectId: string
  settings: boolean
} {
  return (
    input.objectType === BreadcrumbObjectType.CHART &&
    typeof input.objectId === 'string' &&
    input.settings === true
  )
}

export function isNewChartWebPathInput(input: UnifiedWebPathInput): input is {
  objectType: BreadcrumbObjectType.CHART
  workspaceId: string
} {
  return input.objectType === BreadcrumbObjectType.CHART && typeof input.workspaceId === 'string'
}

export function isPivotTablePageWebPathInput(input: UnifiedWebPathInput): input is {
  objectType: BreadcrumbObjectType.PIVOT_TABLE
  objectId: string
} {
  return (
    input.objectType === BreadcrumbObjectType.PIVOT_TABLE &&
    typeof input.objectId === 'string' &&
    !input.settings
  )
}

export function isPivotTableSettingsWebPathInput(input: UnifiedWebPathInput): input is {
  objectType: BreadcrumbObjectType.PIVOT_TABLE
  objectId: string
  settings: boolean
} {
  return (
    input.objectType === BreadcrumbObjectType.PIVOT_TABLE &&
    typeof input.objectId === 'string' &&
    input.settings === true
  )
}

/** A PROJECT path input: a project is a root workspace, so `objectId` is the root workspace id. */
export function isProjectWebPathInput(input: UnifiedWebPathInput): input is {
  objectType: BreadcrumbObjectType.PROJECT
  objectId: string
} {
  return input.objectType === BreadcrumbObjectType.PROJECT && typeof input.objectId === 'string'
}

export function isTableRowWebPathInput(input: UnifiedWebPathInput): input is {
  objectType: BreadcrumbObjectType.TABLE_ROW
  objectId: string
  commentId?: string
  workspaceId?: string
} {
  return input.objectType === BreadcrumbObjectType.TABLE_ROW && typeof input.objectId === 'string'
}

export function isFormsHomeWebPathInput(input: UnifiedWebPathInput): input is {
  objectType: BreadcrumbObjectType.FORM
  objectId: string
  home?: boolean
  settings?: boolean
  settingsParams?: {
    tab: FormEditTabType
    settingsTab: FormSettingsTabType
  }
  variantId?: string
} {
  return input.home === true && input.objectType === BreadcrumbObjectType.FORM
}

export function isFormWebPathInput(input: UnifiedWebPathInput): input is
  | {
      objectType: BreadcrumbObjectType.FORM
      objectId: string
      home?: boolean
      settings?: boolean
      settingsParams?: {
        tab: FormEditTabType
        settingsTab: FormSettingsTabType
      }
      variantId?: string
    }
  | {
      objectType: BreadcrumbObjectType.FORM
      workspaceId?: string
    }
  | {
      objectType: BreadcrumbObjectType.FORM
      creating: boolean
    }
  | {
      objectType: BreadcrumbObjectType.FORM
      objectId: string
      readOnly?: boolean
      variantId?: string
    } {
  const hasObjectId = typeof input.objectId === 'string'
  const isHome = input.home === true
  const isCreatingForm = input.creating === true || input.workspaceId

  if (input.objectType !== BreadcrumbObjectType.FORM) {
    return false
  }

  // Check for the creating variant
  if (isCreatingForm) {
    return !hasObjectId
  }

  // Check for the home variant
  if (isHome) {
    return !hasObjectId && !isCreatingForm
  }

  // Check for the objectId variants
  if (hasObjectId) {
    return !isCreatingForm
  }

  return false
}

export function isDashboardWebPathInput(input: UnifiedWebPathInput): input is {
  objectType: BreadcrumbObjectType.WORKSPACE
  objectId: string
  creating?: boolean
} {
  return input.objectType === BreadcrumbObjectType.WORKSPACE && typeof input.objectId === 'string'
}

export function isDocumentWebPathInput(input: UnifiedWebPathInput): input is {
  objectType: BreadcrumbObjectType.DOCUMENT
  objectId: string
  isUnauth?: boolean
} {
  return (
    input.objectType === BreadcrumbObjectType.DOCUMENT &&
    typeof input.objectId === 'string' &&
    (input.isUnauth === undefined || typeof input.isUnauth === 'boolean')
  )
}

export function isTableWebPathInput(input: UnifiedWebPathInput): input is
  | {
      objectType: BreadcrumbObjectType.TABLE
      objectId: string
      workflow?: { id?: string }
      tableParams?: {
        tab: TableDetailsTabType
        id?: string
      }
      settings?: boolean
      createChildTable?: boolean
      settingsParams?: {
        tab?: TableSettingsTabType
        fieldsTab?: TableSettingsFieldsTabType
        groupId?: string
      }
    }
  | {
      objectType: BreadcrumbObjectType.TABLE
      workspaceId: string
    } {
  const hasObjectId = typeof input.objectId === 'string'
  const hasWorkspaceId = typeof input.workspaceId === 'string'
  // Ensure that settings and createChildTable cannot both be true.
  const mutuallyExclusiveSettings = !(input.settings && input.createChildTable)

  // If there is a workflow, settings and createChildTable must be undefined or false and workspaceId must be undefined
  const workflowCompatibility =
    !input.workflow || !(input.settings || input.createChildTable || input.workspaceId)

  // Check that settings and createChildTable are not true without objectId.
  const settingsAndCreateChildTableValid =
    !(input.settings || input.createChildTable) || hasObjectId

  // Valid if:
  // - It has exactly one of objectId or workspaceId,
  // - settings and createChildTable conditions are satisfied,
  // - settings and createChildTable are not both true,
  // - if there is a workflow, settings and createChildTable must be undefined or false.
  return (
    input.objectType === BreadcrumbObjectType.TABLE &&
    // Can only have object id or workspace id, needs at least one, cannot have both
    hasObjectId !== hasWorkspaceId &&
    settingsAndCreateChildTableValid &&
    mutuallyExclusiveSettings &&
    workflowCompatibility
  )
}
